import { useQuestionaireRoute } from '@app/hooks/useQuestionaireRoute';
import { useSnackbar } from '@app/hooks/useSnackbar';
import { useUserValuesData } from '@app/hooks/useUserValuesData';
import { useUserWithCompany } from '@app/hooks/useUserWithCompany';
import { Button } from '@components/ui/Button/Button';
import AllowedValueTypes from '@legalosApi/types/AllowedValueTypes';
import { IQuestion } from '@legalosApi/types/IQuestion';
import { IQuestionnaire } from '@legalosApi/types/IQuestionnaire';
import { ISection } from '@legalosApi/types/ISection';
import { ISelectOption } from '@legalosApi/types/ISelectOption';
import { QuestionnaireState } from '@legalosApi/types/QuestionnaireState';
import { parseSimpleDate } from '@legalosApi/helpers/parseSimpleDate';
import QuestionType from '@legalosApi/types/QuestionType';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import cx from 'classnames';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isSnippetCategoryKey, SERVICE_SELECTION_APP_ID } from '@app/constants/snippetCategoryKeys';
import { isSuperExpertHiddenQuestion, SUPER_EXPERT_HIDDEN_VARIABLES } from '@app/constants/superExpertMode';
import { AdvanceCallback, UpdateMultipleQuestionsCallback, UpdateQuestionCallback } from '../Quest';
import { CompactStep } from '../Step/CompactStep';
import { CompactUrlStep } from '../Step/UrlStep/CompactUrlStep';
import { AnyStep, QuestionStep, UrlStep } from '../utils';

export interface Props {
  onAdvance: AdvanceCallback;
  onUpdateQuestion: UpdateQuestionCallback;
  onUpdateMultipleQuestions?: UpdateMultipleQuestionsCallback;
  questionnaire: IQuestionnaire;
  stepIndex: number;
  steps: Array<AnyStep>;
  revisit: boolean;
}

/**
 * Resolves a user value string to the appropriate AllowedValueType for a given question.
 * Handles type-specific matching (SELECT_ONE by label, SELECT_SOME by JSON array, DATE by regex, etc.)
 * Mirrors the logic in useUserValues.updateFromUserValue.
 */
function resolveValueForQuestion(
  question: IQuestion,
  answer: string,
  applicationId?: string
): AllowedValueTypes | undefined {
  switch (question.type) {
    case QuestionType.TEXT:
    case QuestionType.MULTILINE_TEXT:
      return answer;
    case QuestionType.SELECT_ONE: {
      const option = question.options.find((o) => o.label === answer || o.origLabel === answer);
      return option?.value;
    }
    case QuestionType.SELECT_SOME: {
      let answers: string[];
      try {
        answers = JSON.parse(answer);
      } catch {
        return undefined;
      }
      // Service-selection questions source their options from the snippet
      // catalogue (not question.options) and store service names directly, so
      // the saved array is already the value — use it as-is.
      if (applicationId === SERVICE_SELECTION_APP_ID && isSnippetCategoryKey(question.tags?.variable)) {
        return answers.length ? answers : undefined;
      }
      const options = (
        answers
          .map((a) => question.options.find((o) => o.label === a || o.origLabel === a))
          .filter(Boolean) as ISelectOption[]
      ).map((o) => o.value);
      return options.length ? options : undefined;
    }
    case QuestionType.DATE:
      return parseSimpleDate(answer);
    default:
      return undefined;
  }
}

/**
 * SuperExpertStream renders all questionnaire questions at once as a dense, scrollable form.
 * Instead of navigating page-by-page, the user sees and can edit all questions simultaneously.
 *
 * Key behaviors:
 * - INFO steps (without UPLOAD) are auto-answered and hidden
 * - All other steps render as compact form rows via CompactStep
 * - Steps are grouped by their parent section for visual hierarchy
 * - When answers change, LegalOS may return new questions which appear with animation
 * - A "Finish" button at the bottom validates and completes the questionnaire
 */
export function SuperExpertStream({
  onAdvance,
  onUpdateQuestion,
  onUpdateMultipleQuestions,
  questionnaire,
  steps,
  revisit,
}: Props) {
  const { t } = useTranslation();
  const { jwt, tokenInfo } = useQuestionaireRoute();
  const { enqueueSnackbar } = useSnackbar();
  const { user, principalCompany, questionnaires } = useUserWithCompany();
  const { getValue, userValuesLoading, userValues } = useUserValuesData();
  const [isAutoAnswering, setIsAutoAnswering] = useState(false);
  const [hasFinished, setHasFinished] = useState(false);
  const prevStepIdsRef = useRef<Set<string>>(new Set());
  const [newStepIds, setNewStepIds] = useState<Set<string>>(new Set());
  const processedAutoStepIdsRef = useRef<Set<string>>(new Set());
  const [semiAutoValues, setSemiAutoValues] = useState<Map<string, AllowedValueTypes>>(new Map());

  // Track which steps are new (appeared after a tree update)
  useEffect(() => {
    const currentIds = new Set(steps.map((s) => s.id));
    const prevIds = prevStepIdsRef.current;

    if (prevIds.size > 0) {
      const added = new Set<string>();
      currentIds.forEach((id) => {
        if (!prevIds.has(id)) added.add(id);
      });
      if (added.size > 0) {
        setNewStepIds(added);
        // Clear "new" highlight after animation
        setTimeout(() => setNewStepIds(new Set()), 2000);
      }
    }

    prevStepIdsRef.current = currentIds;
  }, [steps]);

  // Auto-answer INFO/AUTO steps and pre-fill SEMIAUTO steps using user values.
  // This replicates the updateFromUserValue logic from QuestionStep/ChoiceStep/MessageStep
  // so that AUTO questions are resolved from PersistentState (user values) and SEMIAUTO
  // questions are pre-filled for the user to confirm.
  useEffect(() => {
    // Wait for user values to be available before processing
    if (userValuesLoading || (user && !userValues && !tokenInfo) || (tokenInfo && !userValues)) {
      return;
    }

    const localQuestionnaire = questionnaires.find((q) => q.questionnaireId === questionnaire._id);
    const autoAnswerUpdates: Array<{ _id: string; value: AllowedValueTypes }> = [];
    const newSemiAutoValues = new Map(semiAutoValues);

    for (const step of steps) {
      // Skip already processed steps
      if (processedAutoStepIdsRef.current.has(step.id)) continue;

      const q = (step as QuestionStep).question;
      if (!q) continue;
      if (q.state === QuestionnaireState.COMPLETE) {
        processedAutoStepIdsRef.current.add(step.id);
        continue;
      }

      const tags = q.tags;
      const isAuto = tags.process.includes('AUTO');
      const isInfo = tags.process.includes('INFO');
      const isSemiAuto = tags.process.includes('SEMIAUTO');
      const isUpload = tags.process.includes('UPLOAD');
      const hasKnoten = q.options?.find((o) => o.origLabel.startsWith('Knoten')) !== undefined;
      const variable = tags.variable;

      // --- DPO hack (matches useUserValues behavior) ---
      if (
        q.questionText === 'Soll *Dieter* dein Datenschutzbeauftragter werden?' &&
        (principalCompany?.isDpo || tokenInfo)
      ) {
        const dpoValue = q.options.find((o) => o.origLabel.startsWith('Nein'))?.value;
        if (dpoValue) {
          autoAnswerUpdates.push({ _id: q._id, value: dpoValue });
          processedAutoStepIdsRef.current.add(step.id);
          continue;
        }
      }

      // --- INFO without UPLOAD or Knoten → auto-answer with options[0] ---
      if ((isInfo && !isUpload) || hasKnoten) {
        if (q.options?.[0]) {
          autoAnswerUpdates.push({ _id: q._id, value: q.options[0].value });
          processedAutoStepIdsRef.current.add(step.id);
        }
        continue;
      }

      // --- Hard-hidden system questions (DIETER_SUBSCRIPTION, COHORT, IS_WHITELABEL) ---
      // Never rendered in super-expert mode; always answered from the server-forced
      // user value, with a safe fallback so a hidden question can never block completion.
      if (isSuperExpertHiddenQuestion(q)) {
        const answer = getValue(variable);
        const resolvedValue = answer ? resolveValueForQuestion(q, answer, questionnaire.application.id) : undefined;
        const fallbackValue = q.options?.find((o) => o.origLabel === SUPER_EXPERT_HIDDEN_VARIABLES[variable])?.value;
        const value = resolvedValue ?? fallbackValue;
        if (value !== undefined) {
          if (resolvedValue === undefined) {
            console.warn(`Super expert mode: no user value for hidden variable ${variable}, using fallback`);
          }
          autoAnswerUpdates.push({ _id: q._id, value });
          processedAutoStepIdsRef.current.add(step.id);
        } else {
          console.error(`Super expert mode: cannot answer hidden variable ${variable} (no matching option)`);
        }
        continue;
      }

      // --- AUTO/SEMIAUTO with variable → resolve value from user values (PersistentState) ---
      if ((isAuto || isSemiAuto) && variable) {
        let index: string | undefined;
        if (tags.process.includes('INDEXED_VARIABLE')) {
          index = localQuestionnaire?.userValueIndex;
        }

        const answer = getValue(variable, index);
        if (answer) {
          const resolvedValue = resolveValueForQuestion(q, answer, questionnaire.application.id);
          if (resolvedValue !== undefined) {
            // in super-expert mode we do not need a distinction between AUTO and SEMIAUTO since the user sees all questions at once and can edit any pre-filled value before
            // submitting. So we treat SEMIAUTO as AUTO and directly apply the resolved value.
            autoAnswerUpdates.push({ _id: q._id, value: resolvedValue });
            processedAutoStepIdsRef.current.add(step.id);

            continue;
          }
        }
        // No user value found — show as regular question (matches step component behavior)
      }
    }

    // Update SEMIAUTO pre-fills
    if (newSemiAutoValues.size !== semiAutoValues.size) {
      setSemiAutoValues(newSemiAutoValues);
    }

    // Batch auto-answer all collected updates
    if (autoAnswerUpdates.length > 0) {
      setIsAutoAnswering(true);
      const doAutoAnswer = async () => {
        try {
          if (autoAnswerUpdates.length === 1) {
            await onUpdateQuestion(autoAnswerUpdates[0]._id, autoAnswerUpdates[0].value, true, jwt);
          } else if (onUpdateMultipleQuestions) {
            await onUpdateMultipleQuestions(autoAnswerUpdates, true, jwt);
          } else {
            for (const update of autoAnswerUpdates) {
              await onUpdateQuestion(update._id, update.value, true, jwt);
            }
          }
        } catch (error) {
          console.error('Super expert mode: error auto-answering steps', error);
        } finally {
          setIsAutoAnswering(false);
        }
      };
      doAutoAnswer();
    }
  }, [steps.length, userValues, userValuesLoading]); // Re-run when steps change or user values load

  // Group visible steps by their top-level parent section
  const sectionGroups = useMemo(() => {
    const groups: Array<{ section: ISection; steps: AnyStep[] }> = [];
    let currentGroup: { section: ISection; steps: AnyStep[] } | null = null;

    for (const step of steps) {
      const q = (step as QuestionStep).question;
      if (!q) continue;

      // The synthetic website-URL step is a frontend-only step with no LegalOS
      // counterpart; it has no place in the dense super-expert form.
      if (step.type === 'url') continue;

      // Skip INFO without UPLOAD (auto-answered), including legacy Knoten questions
      const isInfoLike = q.tags.process.includes('INFO') || q.options?.some((o) => o.origLabel.startsWith('Knoten'));
      if (isInfoLike && !q.tags.process.includes('UPLOAD')) continue;

      // Hard-hidden system questions — auto-answered but never shown to the user
      if (isSuperExpertHiddenQuestion(q)) continue;

      // Use the deepest meaningful parent as section header
      const sectionParent = step.parents.length > 1 ? step.parents[step.parents.length - 2] : step.parents[0];

      if (!currentGroup || currentGroup.section._id !== sectionParent?._id) {
        currentGroup = { section: sectionParent || step.parents[0], steps: [] };
        groups.push(currentGroup);
      }

      currentGroup.steps.push(step);
    }

    return groups;
  }, [steps]);

  // Count incomplete questions (excluding auto-answered INFO/AUTO steps)
  const { totalVisible, totalComplete, incompleteSteps } = useMemo(() => {
    let total = 0;
    let complete = 0;
    const incomplete: AnyStep[] = [];

    for (const step of steps) {
      const q = (step as QuestionStep).question;
      if (!q) continue;
      // Frontend-only website-URL step — not a real questionnaire question.
      if (step.type === 'url') continue;
      // Always skip INFO without UPLOAD (these are always auto-answered, never shown), including legacy Knoten questions
      const isInfoLike = q.tags.process.includes('INFO') || q.options?.some((o) => o.origLabel.startsWith('Knoten'));
      if (isInfoLike && !q.tags.process.includes('UPLOAD')) continue;

      // Hard-hidden system questions — auto-answered but never shown to the user
      if (isSuperExpertHiddenQuestion(q)) continue;

      total++;
      if (q.state === QuestionnaireState.COMPLETE) {
        complete++;
      } else {
        incomplete.push(step);
      }
    }

    return { totalVisible: total, totalComplete: complete, incompleteSteps: incomplete };
  }, [steps]);

  const progress = totalVisible > 0 ? totalComplete / totalVisible : 0;
  const isAllComplete = totalComplete === totalVisible && totalVisible > 0;

  // The synthetic website-URL step (present only in the DSE questionnaire) is
  // skipped by the dense form, so surface its scan entry point separately.
  const urlStep = useMemo(() => steps.find((s) => s.type === 'url'), [steps]);
  const submittedWebsiteUrl =
    typeof (urlStep as UrlStep | undefined)?.question?.value === 'string'
      ? ((urlStep as UrlStep).question.value as string)
      : null;

  // Handle finish
  const handleFinish = useCallback(() => {
    if (!isAllComplete) {
      // Scroll to first incomplete question
      const firstIncomplete = incompleteSteps[0];
      if (firstIncomplete) {
        const el = document.getElementById(`compact-step-${firstIncomplete.id}`);
        el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      enqueueSnackbar(
        t(
          'components.block.quest.super_expert_mode.incomplete',
          `Noch ${incompleteSteps.length} unbeantwortete Fragen.`,
          { count: incompleteSteps.length }
        ),
        { variant: 'warning' }
      );
      return;
    }

    setHasFinished(true);
    // Trigger the completion flow by advancing past the last step
    onAdvance(steps[steps.length - 1].id, 'forward');
  }, [isAllComplete, incompleteSteps, steps, onAdvance, enqueueSnackbar, t]);

  return (
    <div className="md:grow-6 max-md:flex-1 md:basis-0 relative md:mt-0 mb-32 pr-4 md:pr-0">
      {/* Progress bar — sticks to top on scroll */}
      <div className="sticky top-0 z-10 -mx-4 px-4 pt-4 pb-3 mb-4 bg-white shadow-[0_4px_6px_-4px_rgba(0,0,0,0.08)]">
        <div className="flex items-center gap-3">
          <div className="flex-1 h-1 bg-slate-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-primary-root rounded-full transition-all duration-500"
              style={{ width: `${progress * 100}%` }}
            />
          </div>
          <span className="text-xs text-slate-400 tabular-nums shrink-0">
            {totalComplete}/{totalVisible}
          </span>
        </div>

        {isAutoAnswering && (
          <div className="text-xs text-slate-400 mt-2 animate-pulse">
            {t(
              'components.block.quest.super_expert_mode.auto_answering',
              'Informationsschritte werden übersprungen...'
            )}
          </div>
        )}
      </div>

      {/* Questions grouped by section */}
      <div className="flex flex-col gap-6">
        {urlStep && (
          <div className="px-1">
            <CompactUrlStep submittedUrl={submittedWebsiteUrl} />
          </div>
        )}
        {sectionGroups.map((group) => (
          <div key={group.section._id} id={`super-expert-section-${group.section._id}`} className="scroll-mt-16">
            {/* Section header */}
            <h3 className="text-sm font-semibold text-slate-600 uppercase tracking-wide mb-3 px-1">
              {group.section.title}
            </h3>

            {/* Questions in this section */}
            <div className="flex flex-col gap-2">
              {group.steps.map((step) => (
                <div
                  key={step.id}
                  id={`compact-step-${step.id}`}
                  className={cx('transition-all duration-500', {
                    'animate-slideIn': newStepIds.has(step.id),
                  })}
                >
                  <CompactStep
                    step={step}
                    onUpdateQuestion={onUpdateQuestion}
                    isNew={newStepIds.has(step.id)}
                    prefillValue={semiAutoValues.get((step as QuestionStep).question?._id)}
                    applicationId={questionnaire.application.id}
                  />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Finish button */}
      <div className="mt-8 pb-8 flex flex-col items-center gap-3">
        {isAllComplete ? (
          <div className="flex items-center gap-2 text-green-600 mb-2">
            <CheckCircleIcon fontSize="small" />
            <span className="text-sm font-medium">
              {t('components.block.quest.super_expert_mode.all_complete', 'Alle Fragen beantwortet')}
            </span>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-amber-600 mb-2">
            <ErrorOutlineIcon fontSize="small" />
            <span className="text-sm">
              {t(
                'components.block.quest.super_expert_mode.remaining',
                `Noch ${incompleteSteps.length} unbeantwortete Fragen`,
                { count: incompleteSteps.length }
              )}
            </span>
          </div>
        )}

        <Button disabled={hasFinished} onClick={handleFinish} googleTriggerClassName="ga4-checkup-finished">
          {t('components.block.quest.super_expert_mode.finish', 'Fragebogen abschließen')}
        </Button>
      </div>
    </div>
  );
}
